import { useContext, useEffect, useState } from "react";
import {
  getMappedFields,
  getTransactionFields,
  createTransactionFields,
  updateTransactionFields,
  deleteTransactionFields,
} from "../services/configurations/fields";
import { updateTransactionProperty } from "../services/configurations/transactions";
import { MappedField, TransactionFieldData } from "../interfaces";
import { AppContext } from "../context";

type FieldType = "PROPERTY" | "FIELD";
type ActionType = "CREATE" | "UPDATE" | "DELETE";

interface FieldsData {
  mappedFieldData: MappedField[];
  transactionFieldData: TransactionFieldData[];
}

const fieldsDataDefaultValues = {
  mappedFieldData: [],
  transactionFieldData: []
}

const handleTransactionFieldActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createTransactionFields,
        fnMsg: t("Transactions.transactionFieldApiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateTransactionFields,
        fnMsg: t("Transactions.transactionFieldApiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteTransactionFields,
        fnMsg: t("Transactions.transactionFieldApiDeleteLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

const handleTransactionPropertyActionFunction = (
  action: ActionType,
  t: Function
) => {
  switch (action) {
    case "UPDATE":
      return {
        actionFn: updateTransactionProperty,
        fnMsg: t("Transactions.transactionPropertyApiUpdateLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useTransactionFields = (
  serviceApi?: string,
) => {
  const {
    t,
    toast,
    appState: { selectedApp, selectedTransaction },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<FieldsData | null>(null);
  const [filteredData, setFilteredData] = useState<TransactionFieldData[] | null>(null);
  const [selectedFieldsIds, setSelectedFieldsIds] = useState<Array<number>>([]);

  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || '0';

  const reloadFn = () => {
    setData(fieldsDataDefaultValues);
    setFilteredData(null)
    setReload((prev) => prev + 1);
  };

  const transactionFieldAction = async (
    field: FieldType,
    action: ActionType,
    data: any
  ) => {
    const { actionFn, fnMsg } =
      field === "FIELD"
        ? handleTransactionFieldActionFunction(action, t)
        : handleTransactionPropertyActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (serviceApi && appid) {
      (async function () {
        await Promise.all([
          getMappedFields(appid, serviceApi),
          getTransactionFields(appid, selectedTransaction?.id || 0),
        ])
          .then((response) => {
            const mappedFields: MappedField[] = response[0]?.data;
            const transactionFields: TransactionFieldData[] = response[1]?.data;
            setData({
              mappedFieldData: mappedFields,
              transactionFieldData: transactionFields,
            });
            setFilteredData(transactionFields)
            setSelectedFieldsIds(transactionFields.map(field => field.mappedfieldid))
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [
    appid,
    toast,
    selectedTransaction,
    reload,
    serviceApi,
  ]);

  return {
    mappedFields: data?.mappedFieldData,
    transactionFields: data?.transactionFieldData,
    filteredData,
    setFilteredData,
    selectedFieldsIds,
    transactionFieldAction,
    reloadFn,
  };
};
